<template>
  <q-dialog v-model="showDialog" persistent>
    <q-card style="min-width: 600px">
      <q-card-section class="row items-center q-pb-none">
        <div class="text-h6">{{ isEditing ? 'Edit Component' : 'Add Component' }}</div>
        <q-space />
        <q-btn icon="close" flat round dense @click="handleCancel" />
      </q-card-section>

      <q-card-section>
        <!-- Component Role/Label -->
        <q-input
          v-model="componentData.label"
          label="Component Label"
          outlined
          class="q-mb-md"
          :rules="[val => !!val || 'Label is required']"
          hint="Descriptive name for this component (e.g., 'Supply Fan', 'Cooling Valve')"
          @update:model-value="autoGenerateRole"
        />

        <!-- Component Role (internal identifier) -->
        <q-input
          v-model="componentData.role"
          label="Role Identifier"
          outlined
          class="q-mb-md"
          :readonly="isEditing || componentData.role === 'parent'"
          :rules="[val => !!val || 'Role is required', val => /^[a-z_][a-z0-9_]*$/.test(val) || 'Use lowercase letters, numbers, and underscores only']"
          hint="Internal identifier (e.g., 'supply_fan', 'cooling_valve'). Cannot be changed after creation."
        />

        <!-- BRICK Class Selection -->
        <q-select
          v-model="componentData.brick_class"
          :options="filteredBrickClassOptions"
          label="BRICK Class"
          outlined
          class="q-mb-md"
          emit-value
          map-options
          use-input
          input-debounce="0"
          option-value="value"
          option-label="label"
          :rules="[val => !!val || 'BRICK class is required']"
          hint="Select the BRICK ontology class for this component"
          @filter="filterBrickClasses"
        />

        <!-- Template Selection (for equipment or device templates) -->
        <q-select
          v-model="componentData.template_id"
          :options="templateOptions"
          label="Link to Device Template"
          outlined
          clearable
          class="q-mb-md"
          emit-value
          map-options
          option-value="id"
          option-label="name"
          :loading="loadingTemplates"
          hint="Select a device template to auto-fill BRICK class and default points"
          @update:model-value="onTemplateSelect"
        >
          <template #no-option>
            <q-item>
              <q-item-section class="text-grey">
                No templates available
              </q-item-section>
            </q-item>
          </template>
          <template #option="scope">
            <q-item v-bind="scope.itemProps">
              <q-item-section>
                <q-item-label>{{ scope.opt.name }}</q-item-label>
                <q-item-label caption>
                  {{ formatBrickClass(scope.opt.brick_class) }}
                  <span v-if="scope.opt.manufacturer"> · {{ scope.opt.manufacturer }}</span>
                  <span v-if="scope.opt.points?.length"> · {{ scope.opt.points.length }} points</span>
                  <span v-if="scope.opt.usage_count"> · Used {{ scope.opt.usage_count }}x</span>
                </q-item-label>
              </q-item-section>
              <q-item-section v-if="scope.opt.usage_count" side>
                <q-badge color="grey" outline>
                  {{ scope.opt.usage_count }}x
                </q-badge>
              </q-item-section>
            </q-item>
          </template>
        </q-select>

        <!-- Show linked template info -->
        <div v-if="selectedTemplate" class="q-mb-md q-pa-sm bg-orange-1 rounded-borders">
          <div class="text-caption text-grey-7">
            Linked to device template: <strong>{{ selectedTemplate.name }}</strong>
            <span v-if="selectedTemplate.points?.length"> ({{ selectedTemplate.points.length }} points)</span>
          </div>
        </div>

        <!-- Quantity -->
        <div class="row q-col-gutter-md q-mb-md">
          <div class="col-6">
            <q-input
              v-model.number="componentData.min_quantity"
              label="Minimum Quantity"
              outlined
              type="number"
              min="0"
              :rules="[val => val >= 0 || 'Must be 0 or greater']"
              hint="Minimum number of this component (0 = optional)"
            />
          </div>
          <div class="col-6">
            <q-input
              v-model.number="componentData.max_quantity"
              label="Maximum Quantity"
              outlined
              type="number"
              min="1"
              :rules="[val => val >= 1 || 'Must be 1 or greater']"
              hint="Maximum number of this component (blank = unlimited)"
            />
          </div>
        </div>

        <!-- Default Points -->
        <div class="q-mb-md">
          <div class="row items-center q-mb-sm">
            <div class="text-subtitle2">Default Points</div>
            <q-space />
            <q-btn
              size="sm"
              flat
              dense
              icon="add"
              label="Add Point"
              @click="addPoint"
            />
          </div>

          <q-list v-if="componentData.default_points?.length > 0" bordered separator>
            <q-item v-for="(point, index) in componentData.default_points" :key="index">
              <q-item-section>
                <div class="row q-gutter-sm">
                  <div class="col-5">
                    <q-input
                      v-model="point.name"
                      label="Point Name"
                      outlined
                      dense
                      :rules="[val => !!val || 'Required']"
                    />
                  </div>
                  <div class="col-3">
                    <q-select
                      v-model="point.point_type"
                      :options="pointTypeOptions"
                      label="Type"
                      outlined
                      dense
                      emit-value
                      map-options
                    />
                  </div>
                  <div class="col-3">
                    <q-select
                      v-model="point.brick_class"
                      :options="pointBrickClassOptions"
                      label="BRICK Class"
                      outlined
                      dense
                    />
                  </div>
                  <div class="col-1">
                    <q-btn
                      icon="delete"
                      flat
                      round
                      dense
                      size="sm"
                      color="negative"
                      @click="removePoint(index)"
                    />
                  </div>
                </div>
              </q-item-section>
            </q-item>
          </q-list>
          <div v-else class="text-center q-pa-md bg-grey-2 rounded-borders">
            <div class="text-caption text-grey-7">No default points configured</div>
          </div>
        </div>

        <!-- Relationships to Other Components -->
        <div v-if="isEditing && availableComponents.length > 0">
          <div class="row items-center q-mb-sm">
            <div class="text-subtitle2">Relationships</div>
            <q-space />
            <q-btn
              size="sm"
              flat
              dense
              icon="add"
              label="Add Relationship"
              @click="addRelationship"
            />
          </div>

          <q-list v-if="componentData.relationships?.length > 0" bordered separator>
            <q-item v-for="(rel, index) in componentData.relationships" :key="index">
              <q-item-section>
                <div class="row q-gutter-sm items-center">
                  <div class="col-4">
                    <q-select
                      v-model="rel.type"
                      :options="relationshipTypeOptions"
                      label="Relationship Type"
                      outlined
                      dense
                    />
                  </div>
                  <div class="col-4">
                    <q-select
                      v-model="rel.target"
                      :options="availableComponents"
                      label="Target Component"
                      outlined
                      dense
                      option-value="role"
                      option-label="label"
                      emit-value
                      map-options
                    />
                  </div>
                  <div class="col-3">
                    <q-select
                      v-model="rel.direction"
                      :options="directionOptions"
                      label="Direction"
                      outlined
                      dense
                    />
                  </div>
                  <div class="col-1">
                    <q-btn
                      icon="delete"
                      flat
                      round
                      dense
                      size="sm"
                      color="negative"
                      @click="removeRelationship(index)"
                    />
                  </div>
                </div>
              </q-item-section>
            </q-item>
          </q-list>
          <div v-else class="text-center q-pa-md bg-grey-2 rounded-borders">
            <div class="text-caption text-grey-7">No relationships configured</div>
          </div>
        </div>

        <!-- Metadata (Optional) -->
        <q-expansion-item
          label="Advanced Settings"
          icon="settings"
          class="q-mt-md"
        >
          <q-card flat bordered>
            <q-card-section>
              <q-input
                v-model="metadataString"
                label="Metadata (JSON)"
                outlined
                type="textarea"
                rows="3"
                hint="Optional metadata as JSON object"
                :error="!!metadataError"
                :error-message="metadataError"
                @update:model-value="validateMetadata"
              />
            </q-card-section>
          </q-card>
        </q-expansion-item>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn flat label="Cancel" @click="handleCancel" />
        <q-btn
          flat
          label="Save"
          color="primary"
          :disable="!isValid"
          @click="handleSave"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup>
  import { ref, computed, watch, onMounted } from 'vue';
  import { useQuasar } from 'quasar';
  import api from '../../services/api';
  import { formatBrickClass } from '@/composables/brick-schema/operations/useBrickValidation.js';

  const props = defineProps({
    modelValue: {
      type: Boolean,
      default: false,
    },
    component: {
      type: Object,
      default: null,
    },
    existingComponents: {
      type: Array,
      default: () => [],
    },
  });

  const emit = defineEmits(['update:modelValue', 'save']);

  const $q = useQuasar();

  // Dialog state
  const showDialog = ref(false);

  // Component data
  const componentData = ref({
    label: '',
    role: '',
    brick_class: '',
    template_id: null,
    min_quantity: 0,
    max_quantity: null,
    default_points: [],
    relationships: [],
    metadata: {},
  });

  // Form state
  const loadingTemplates = ref(false);
  const templateOptions = ref([]);
  const metadataString = ref('{}');
  const metadataError = ref('');

  // Options - comprehensive BRICK class list
  const brickClassOptions = [
    // Equipment
    { label: 'Air Handling Unit', value: 'brick:Air_Handling_Unit' },
    { label: 'Variable Air Volume Box', value: 'brick:Variable_Air_Volume_Box' },
    { label: 'Fan Coil Unit', value: 'brick:Fan_Coil_Unit' },
    { label: 'Chiller', value: 'brick:Chiller' },
    { label: 'Boiler', value: 'brick:Boiler' },
    { label: 'Heat Pump', value: 'brick:Heat_Pump' },
    { label: 'Cooling Tower', value: 'brick:Cooling_Tower' },
    { label: 'Heat Exchanger', value: 'brick:Heat_Exchanger' },
    // Mechanical
    { label: 'Fan', value: 'brick:Fan' },
    { label: 'Pump', value: 'brick:Pump' },
    { label: 'Damper', value: 'brick:Damper' },
    { label: 'Valve', value: 'brick:Valve' },
    { label: 'Filter', value: 'brick:Filter' },
    { label: 'Motor', value: 'brick:Motor' },
    // Sensors
    { label: 'Sensor (Generic/Multi)', value: 'brick:Sensor' },
    { label: 'Temperature Sensor', value: 'brick:Temperature_Sensor' },
    { label: 'Pressure Sensor', value: 'brick:Pressure_Sensor' },
    { label: 'Differential Pressure Sensor', value: 'brick:Differential_Pressure_Sensor' },
    { label: 'Flow Sensor', value: 'brick:Flow_Sensor' },
    { label: 'Humidity Sensor', value: 'brick:Humidity_Sensor' },
    { label: 'CO2 Sensor', value: 'brick:CO2_Sensor' },
    { label: 'Air Quality Sensor', value: 'brick:Air_Quality_Sensor' },
    { label: 'Current Sensor', value: 'brick:Current_Sensor' },
    { label: 'Occupancy Sensor', value: 'brick:Occupancy_Sensor' },
    // Controls & Electrical
    { label: 'Controller', value: 'brick:Controller' },
    { label: 'Actuator', value: 'brick:Actuator' },
    { label: 'Variable Frequency Drive', value: 'brick:Variable_Frequency_Drive' },
    { label: 'Meter', value: 'brick:Meter' },
  ];

  const pointTypeOptions = [
    { label: 'Sensor', value: 'sensor' },
    { label: 'Actuator', value: 'actuator' },
    { label: 'Setpoint', value: 'setpoint' },
    { label: 'Status', value: 'status' },
    { label: 'Command', value: 'command' },
  ];

  const pointBrickClassOptions = [
    'brick:Temperature_Sensor',
    'brick:Pressure_Sensor',
    'brick:Flow_Sensor',
    'brick:Humidity_Sensor',
    'brick:CO2_Sensor',
    'brick:Setpoint',
    'brick:Temperature_Setpoint',
    'brick:Status',
    'brick:Command',
    'brick:Sensor',
    'brick:Point',
  ];

  const relationshipTypeOptions = [
    'feeds',
    'supplies',
    'controls',
    'monitors',
    'hasPoint',
    'hasPart',
    'isPartOf',
    'isLocatedIn',
  ];

  const directionOptions = [
    { label: 'From this to target', value: 'from' },
    { label: 'From target to this', value: 'to' },
    { label: 'Bidirectional', value: 'both' },
  ];

  // Filtered BRICK class options for search
  const filteredBrickClassOptions = ref([...brickClassOptions]);

  const filterBrickClasses = (val, update) => {
    update(() => {
      if (!val) {
        filteredBrickClassOptions.value = [...brickClassOptions];
      } else {
        const needle = val.toLowerCase();
        filteredBrickClassOptions.value = brickClassOptions.filter(
          (opt) => opt.label.toLowerCase().includes(needle) || opt.value.toLowerCase().includes(needle),
        );
      }
    });
  };

  // Computed
  const isEditing = computed(() => !!props.component);

  const availableComponents = computed(() => {
    return props.existingComponents.filter((c) => c.role !== componentData.value.role);
  });

  const isValid = computed(() => {
    return !!(
      componentData.value.label &&
      componentData.value.role &&
      componentData.value.brick_class &&
      !metadataError.value
    );
  });

  // Methods
  const loadTemplates = async () => {
    loadingTemplates.value = true;
    try {
      // Compound templates define device relationships, so only load device templates
      const deviceRes = await api.get('/v2/device-templates');
      const devices = (deviceRes.data || deviceRes || []).map((t) => ({
        ...t,
        type: 'device',
      }));

      templateOptions.value = devices;

      // If editing a component with a template_id, find and set the selected template
      if (componentData.value.template_id) {
        selectedTemplate.value = templateOptions.value.find((t) => t.id === componentData.value.template_id) || null;
      }
    } catch (error) {
      console.error('Failed to load device templates:', error);
    } finally {
      loadingTemplates.value = false;
    }
  };

  // Track the selected template object for display
  const selectedTemplate = ref(null);

  const onTemplateSelect = (templateId) => {
    if (templateId) {
      const template = templateOptions.value.find((t) => t.id === templateId);
      if (template) {
        selectedTemplate.value = template;

        // Auto-fill BRICK class from template
        if (template.brick_class) {
          componentData.value.brick_class = template.brick_class;
        }

        // Auto-fill label if empty
        if (!componentData.value.label) {
          componentData.value.label = template.name;
        }

        // Auto-generate role from label if empty
        if (!componentData.value.role) {
          componentData.value.role = template.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
        }

        // Auto-fill default points from template if none configured
        if ((!componentData.value.default_points || componentData.value.default_points.length === 0) && template.points?.length) {
          componentData.value.default_points = template.points.map((p) => ({
            name: p.name,
            point_type: p.point_type || 'sensor',
            brick_class: p.brick_class || 'brick:Sensor',
          }));
        }
      }
    } else {
      selectedTemplate.value = null;
    }
  };

  // Auto-generate role from label for new components
  const autoGenerateRole = (label) => {
    if (!isEditing.value && componentData.value.role !== 'parent') {
      componentData.value.role = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    }
  };

  const addPoint = () => {
    if (!componentData.value.default_points) {
      componentData.value.default_points = [];
    }
    componentData.value.default_points.push({
      name: '',
      point_type: 'sensor',
      brick_class: 'brick:Sensor',
    });
  };

  const removePoint = (index) => {
    componentData.value.default_points.splice(index, 1);
  };

  const addRelationship = () => {
    if (!componentData.value.relationships) {
      componentData.value.relationships = [];
    }
    componentData.value.relationships.push({
      type: 'feeds',
      target: '',
      direction: 'from',
    });
  };

  const removeRelationship = (index) => {
    componentData.value.relationships.splice(index, 1);
  };

  const validateMetadata = (value) => {
    try {
      if (value.trim()) {
        componentData.value.metadata = JSON.parse(value);
        metadataError.value = '';
      } else {
        componentData.value.metadata = {};
        metadataError.value = '';
      }
    } catch (e) {
      metadataError.value = 'Invalid JSON: ' + e.message;
    }
  };

  const handleCancel = () => {
    showDialog.value = false;
  };

  const handleSave = () => {
    if (!isValid.value) {return;}

    // Prepare the component data
    const result = {
      ...componentData.value,
      // Ensure role is lowercase with underscores
      role: componentData.value.role.toLowerCase().replace(/\s+/g, '_'),
      // Remove empty arrays
      default_points: componentData.value.default_points?.length ? componentData.value.default_points : undefined,
      relationships: componentData.value.relationships?.length ? componentData.value.relationships : undefined,
    };

    emit('save', result);
    showDialog.value = false;
  };

  // Watchers
  watch(() => props.modelValue, (newVal) => {
    showDialog.value = newVal;
    if (newVal) {
      if (props.component) {
        // Editing existing component
        componentData.value = {
          ...props.component,
          min_quantity: props.component.min_quantity ?? 0,
          max_quantity: props.component.max_quantity ?? null,
          default_points: props.component.default_points || [],
          relationships: props.component.relationships || [],
          metadata: props.component.metadata || {},
        };
        metadataString.value = JSON.stringify(componentData.value.metadata, null, 2);
        selectedTemplate.value = null; // Will be set after templates load if template_id exists
      } else {
        // Adding new component
        componentData.value = {
          label: '',
          role: '',
          brick_class: '',
          template_id: null,
          min_quantity: 0,
          max_quantity: null,
          default_points: [],
          relationships: [],
          metadata: {},
        };
        metadataString.value = '{}';
        selectedTemplate.value = null;
      }
      loadTemplates();
    }
  });

  watch(showDialog, (newVal) => {
    emit('update:modelValue', newVal);
  });
</script>